import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Grid, Paper, Typography, Button, LinearProgress } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import TransactionFilters, { TransactionFiltersState } from '@/components/TransactionFilters'
import TransactionTable, { Column } from '@/components/TransactionTable'
import TransactionTablePagination from '@/components/TransactionTablePagination'
import TransactionDetailModal from '@/components/TransactionDetailModal'
import { SmsWithdrawalRequest } from '@/types/transactions'
import { exportToCSV, exportToPDF } from '@/utils/export'
import { transactionService } from '@/services/transactionService'

const columns: Column<SmsWithdrawalRequest>[] = [
  { header: 'MSISDN', field: 'msisdn' },
  { header: 'Amount', field: 'amount', render: (row) => `KES ${row.amount.toFixed(2)}` },
  { header: 'Link ID', field: 'linkId' },
  { header: 'External ID', field: 'externalTransactionId' },
  { header: 'Created At', field: 'createdAt' },
]

const SmsRequests = () => {
  const [filters, setFilters] = useState<TransactionFiltersState>({
    msisdn: '',
    paybill: '',
    status: 'All',
    channel: 'All',
    externalId: '',
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SmsWithdrawalRequest[]>([])
  const [total, setTotal] = useState(0)
  const [selectedTransaction, setSelectedTransaction] = useState<SmsWithdrawalRequest | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const buildQueryParams = useCallback(() => {
    const params: Record<string, string | number | undefined> = {
      page: page + 1,
      page_size: rowsPerPage,
    }
    if (filters.msisdn) params.msisdn = filters.msisdn
    if (filters.externalId) params.external_transaction_id = filters.externalId
    return params
  }, [filters, page, rowsPerPage])

  const fetchSmsRequests = useCallback(async () => {
    setLoading(true)
    try {
      const response = await transactionService.fetchSmsRequests(buildQueryParams())
      setData(response.items)
      setTotal(response.pagination.total)
    } catch (error) {
      console.error('Failed to load SMS requests', error)
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams])

  useEffect(() => {
    fetchSmsRequests()
  }, [fetchSmsRequests])

  const totalAmount = useMemo(() => data.reduce((acc, item) => acc + item.amount, 0), [data])

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRowClick = (row: SmsWithdrawalRequest) => {
    setSelectedTransaction(row)
    setModalOpen(true)
  }

  const handleExportCSV = () => {
    exportToCSV(data, `sms_requests_${new Date().toISOString().split('T')[0]}`, columns)
  }

  const handleExportPDF = () => {
    exportToPDF(
      data,
      `sms_requests_${new Date().toISOString().split('T')[0]}`,
      'SMS Withdrawal Requests',
      columns
    )
  }

  const handleFilterChange = (updated: TransactionFiltersState) => {
    setFilters(updated)
    setPage(0)
  }

  return (
    <Box>
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <Typography variant="h4" component="h1" gutterBottom>
        SMS Withdrawal Requests
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Requests
            </Typography>
            <Typography variant="h5">{total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Amount Requested
            </Typography>
            <Typography variant="h5">KES {totalAmount.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Linked Withdrawals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Linked withdrawals are reported directly from the core system.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <TransactionFilters onFilterChange={handleFilterChange} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCSV}
          size="small"
        >
          Export CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleExportPDF}
          size="small"
        >
          Export PDF
        </Button>
      </Box>

      <TransactionTable
        title="SMS Withdrawal Requests"
        columns={columns}
        data={data}
        onRowClick={handleRowClick}
      />

      <TransactionTablePagination
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <TransactionDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        transaction={selectedTransaction}
        type="sms-request"
      />
    </Box>
  )
}

export default SmsRequests
